import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { useMemo } from "react";

import BackgroundImage from "../../shared/image/placeholder.jpg";
import InsightsImageCard from "../pricing-reports/design/ImageCard";
import {
  buildFlyerGalleryCardMetaChips,
  buildFlyerGalleryCardPrimaryMetric,
  formatImageCardRetailerProvinceDescription,
  formatInsightsImageCardAriaLabel,
} from "../pricing-reports/pricingReportCardDisplay";

export default function FlyerGalleryCard({ displayItem, thumbnailUrl, onCardClick }) {
  const retailer = useSelector((state) => state.retailer);
  const isCardClickable = Boolean(displayItem?.id) && typeof onCardClick === "function";
  const coverImageKey = `${displayItem?.id ?? "row"}:${thumbnailUrl ?? "placeholder"}`;

  const placeholderImageUrl = retailer?.default_image
    ? `${process.env.REACT_APP_API_URL}/assets/${retailer.default_image}`
    : BackgroundImage;

  const primaryMetric = useMemo(
    () => buildFlyerGalleryCardPrimaryMetric(displayItem),
    [displayItem]
  );
  const metaChips = useMemo(() => buildFlyerGalleryCardMetaChips(displayItem), [displayItem]);
  const productTitle = displayItem?.product || "—";
  const cardDescription = useMemo(
    () =>
      formatImageCardRetailerProvinceDescription(displayItem?.retailer, displayItem?.province),
    [displayItem?.retailer, displayItem?.province]
  );

  const imageCard = (
    <InsightsImageCard
      key={coverImageKey}
      title={productTitle}
      description={cardDescription}
      imageUrl={thumbnailUrl || ""}
      placeholderImageUrl={placeholderImageUrl}
      imageFit="cover"
      primaryMetric={primaryMetric}
      metaChips={metaChips}
    />
  );

  if (!isCardClickable) {
    return <Box sx={{ height: "100%" }}>{imageCard}</Box>;
  }

  const cardAriaLabel = formatInsightsImageCardAriaLabel(
    productTitle,
    cardDescription,
    primaryMetric,
    metaChips
  );

  return (
    <Box
      role="button"
      tabIndex={0}
      aria-label={cardAriaLabel || "Open promotion gallery"}
      onClick={() => onCardClick(displayItem)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onCardClick(displayItem);
        }
      }}
      sx={{
        cursor: "pointer",
        height: "100%",
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      {imageCard}
    </Box>
  );
}
